import { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { MaintenanceRequest } from '../../types/maintenance';

export type TriageDecision = 'NADA' | 'RESOLVE' | 'EU_RESOLVO' | 'CONVERTER_EM_COMPRA';

interface TriageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (decision: TriageDecision, notes?: string) => void;
  request: MaintenanceRequest | null;
  loading?: boolean;
}

export default function TriageModal({ isOpen, onClose, onConfirm, request, loading = false }: TriageModalProps) {
  const [decision, setDecision] = useState<TriageDecision | null>(null);
  const [notes, setNotes] = useState('');

  const handleClose = () => {
    setDecision(null);
    setNotes('');
    onClose();
  };

  if (!isOpen || !request) return null;

  const handleSubmit = () => {
    if (!decision) return;
    onConfirm(decision, notes || undefined);
  };

  // A 4ª opção ("Converter em Compra") só faz sentido em requisições de manutenção.
  // Compras já criadas (subcategory === 'compras') não devem permitir converter em compra.
  const canConvertToPurchase = request.subcategory !== 'compras';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" onClick={handleClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Triar Requisição</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
              disabled={loading}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Request Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-md">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Ativo:</span>{' '}
                <span className="text-gray-900">{request.equipmentId}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Descrição:</span>{' '}
                <span className="text-gray-900">{request.equipmentDescription || 'N/A'}</span>
              </div>
              <div className="col-span-2">
                <span className="font-medium text-gray-700">Problema:</span>{' '}
                <span className="text-gray-900">{request.problemDescription}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Solicitante:</span>{' '}
                <span className="text-gray-900">{request.requester?.name || request.requesterId}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Subcategoria:</span>{' '}
                <span className="text-gray-900 capitalize">{request.subcategory || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Triage Decision */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Decisão de Triagem *
            </label>
            <div className="space-y-3">
              <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${decision === 'NADA' ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-gray-400'}`}>
                <input
                  type="radio"
                  name="triageDecision"
                  value="NADA"
                  checked={decision === 'NADA'}
                  onChange={(e) => setDecision(e.target.value as TriageDecision)}
                  className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500"
                  disabled={loading}
                />
                <div className="ml-3">
                  <span className="block text-sm font-medium text-gray-900">Nada a fazer</span>
                  <span className="block text-sm text-gray-500">Arquivar esta requisição (não requer ação)</span>
                </div>
              </label>

              <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${decision === 'RESOLVE' ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-gray-400'}`}>
                <input
                  type="radio"
                  name="triageDecision"
                  value="RESOLVE"
                  checked={decision === 'RESOLVE'}
                  onChange={(e) => setDecision(e.target.value as TriageDecision)}
                  className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500"
                  disabled={loading}
                />
                <div className="ml-3">
                  <span className="block text-sm font-medium text-gray-900">Resolver</span>
                  <span className="block text-sm text-gray-500">Aprovar para resolução (requerente segue responsável)</span>
                </div>
              </label>

              <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${decision === 'EU_RESOLVO' ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-gray-400'}`}>
                <input
                  type="radio"
                  name="triageDecision"
                  value="EU_RESOLVO"
                  checked={decision === 'EU_RESOLVO'}
                  onChange={(e) => setDecision(e.target.value as TriageDecision)}
                  className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500"
                  disabled={loading}
                />
                <div className="ml-3">
                  <span className="block text-sm font-medium text-gray-900">Eu resolvo</span>
                  <span className="block text-sm text-gray-500">Atribuir a mim mesmo como responsável</span>
                </div>
              </label>

              {canConvertToPurchase && (
                <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${decision === 'CONVERTER_EM_COMPRA' ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-gray-400'}`}>
                  <input
                    type="radio"
                    name="triageDecision"
                    value="CONVERTER_EM_COMPRA"
                    checked={decision === 'CONVERTER_EM_COMPRA'}
                    onChange={(e) => setDecision(e.target.value as TriageDecision)}
                    className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500"
                    disabled={loading}
                  />
                  <div className="ml-3">
                    <span className="block text-sm font-medium text-gray-900">Converter em Compra</span>
                    <span className="block text-sm text-gray-500">
                      Transformar esta requisição numa requisição de compra
                    </span>
                  </div>
                </label>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label htmlFor="triageNotes" className="block text-sm font-medium text-gray-700 mb-2">
              Notas (opcional)
            </label>
            <textarea
              id="triageNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              placeholder="Adicione notas sobre a decisão de triagem..."
              disabled={loading}
            />
          </div>

          {/* Warning for NADA */}
          {decision === 'NADA' && (
            <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <span className="font-medium">Atenção:</span> Ao selecionar &quot;Nada a fazer&quot;, a requisição será arquivada e não aparecerá mais na lista de tarefas pendentes.
              </div>
            </div>
          )}

          {/* Warning for CONVERTER_EM_COMPRA */}
          {decision === 'CONVERTER_EM_COMPRA' && (
            <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <span className="font-medium">Atenção:</span> Ao selecionar &quot;Converter em Compra&quot;, a requisição mantém o mesmo ID mas a subcategoria passa a <em>compras</em> e o estado para <strong>PENDENTE</strong>. O solicitante será notificado por email e os gerentes elegíveis serão notificados como se uma nova compra tivesse sido criada.
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!decision || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processando...' : 'Confirmar Triagem'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
